import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { Reveal } from '@/components/generals/reveal'
import { ButtonLink } from '@/components/generals/button'
import { Blueprint, DarkBand } from '@/components/generals/dark-band'
import { GradientTabs } from '@/components/generals/gradient-tabs'
import { ChatCard } from '@/components/generals/chat/chat-card'
import { ChatComposer } from '@/components/generals/chat/chat-composer'
import { EmailCapture } from '@/components/generals/chat/email-capture'
import { useChatScript, useVisitorChat } from '@/components/generals/chat/use-chat'
import { fullServiceTabs } from '@/constants/home'

const PERSONA = {
    initials: 'DR',
    name: "Daniel Roth",
    role: "Head of Supply Chain"
}

/**
 * The dark cinematic band. Tabs auto-advance and replay their conversation
 * until the visitor picks a tab or starts typing; then everything stays put.
 */
export const FullService: FC = (): ReactNode => {
    const stageRef = useRef<HTMLDivElement>(null)
    const inView = useInView(stageRef, { once: true, threshold: 0.3 })
    const visible = useInView(stageRef, { threshold: 0.05 })
    const reduced = useReducedMotion()
    const [active, setActive] = useState(0)
    const [auto, setAuto] = useState(true)
    const [interacted, setInteracted] = useState(false)
    const [finished, setFinished] = useState<number | null>(null)
    const tab = fullServiceTabs[active]

    const { messages, setMessages, typing, setTyping } = useChatScript({
        script: tab.conversation,
        playKey: active,
        enabled: inView && !interacted,
        onComplete: () => setFinished(active)
    })

    useEffect(() => {
        if (!auto || interacted || reduced || !visible || finished !== active) return

        const timer = window.setTimeout(() => {
            setActive(index => (index + 1) % fullServiceTabs.length)
        }, 5200)

        return () => window.clearTimeout(timer)
    }, [auto, interacted, reduced, visible, finished, active])

    const visitor = useVisitorChat({
        source: 'home-chat',
        topic: 'Full service',
        firstReply: "Thanks. A Quayside solutions engineer will pick this up and reply within one working day. Want to leave your email so we can reach you?",
        followUpReply: "Noted. I've added that to your message.",
        confirmation: email => `Thanks. We'll reply to ${email} within one working day.`,
        setMessages,
        setTyping,
        onStart: () => {
            setInteracted(true)
            setAuto(false)
        }
    })

    const selectTab = (index: number) => {
        setAuto(false)
        setActive(index)
    }

    const busy = visitor.stage === 'replying' || visitor.stage === 'sending'
    const askingEmail = visitor.stage === 'email' || visitor.stage === 'sending'

    return (
        <DarkBand
            id='full-service'
            aria-labelledby='full-service-title'
            className='band-y'
        >
            <div className='shell relative'>
                <div className='grid gap-14 *:min-w-0 sm:gap-16 lg:grid-cols-12 lg:gap-12'>
                    <div className='flex flex-col lg:col-span-6'>
                        <Reveal>
                            <p className='text-sm font-medium text-mist'>
                                Full service
                            </p>

                            <h2 id='full-service-title' className='headline mt-5 text-[clamp(2.2rem,9.4vw,4.5rem)] lg:text-[clamp(2.9rem,6.6vw,5.8rem)]'>
                                <span className='block text-white'>
                                    Freight is hard,
                                </span>

                                <span className='text-aurora'>
                                    let us help.
                                </span>
                            </h2>

                            <p className='mt-7 max-w-xl text-lg leading-relaxed text-mist sm:text-xl'>
                                For teams that want expert hands, Quayside pairs an enterprise-grade integration platform with a dedicated delivery team that scopes, builds, launches and supports every flow.
                            </p>

                            <ButtonLink href='/contact' variant='dark' size='md' className='mt-9'>
                                Talk to our team
                                <ArrowRight aria-hidden='true' className='size-4 text-white/70 transition-transform duration-300 group-hover/btn:translate-x-0.5' />
                            </ButtonLink>
                        </Reveal>

                        <Reveal delay={120} className='mt-12 sm:mt-16 lg:mt-auto lg:pt-24'>
                            <GradientTabs
                                tabs={fullServiceTabs}
                                active={active}
                                onSelect={selectTab}
                                panelId='full-service-panel'
                                idPrefix='full-service'
                                tone='dark'
                            />

                            <div
                                id='full-service-panel'
                                role='tabpanel'
                                aria-labelledby={`full-service-${tab.id}`}
                                className='mt-8 min-h-[10rem] sm:min-h-[8.5rem]'
                            >
                                <p key={tab.id} className='animate-fade-soft text-[clamp(1.125rem,2.1vw,1.65rem)] leading-snug tracking-[-0.02em]'>
                                    <span className='text-white'>
                                        {tab.lead}
                                    </span>{' '}

                                    <span className='text-mist/80'>
                                        {tab.rest}
                                    </span>
                                </p>
                            </div>
                        </Reveal>
                    </div>

                    <div ref={stageRef} className='relative lg:col-span-6 lg:pl-4 xl:pl-10'>
                        <Blueprint className='-inset-x-4 -inset-y-12 sm:-inset-x-10' />

                        <Reveal delay={160} className='relative'>
                            <ChatCard
                                persona={PERSONA}
                                docLines={tab.docLines}
                                docKey={tab.id}
                                messages={messages}
                                typing={typing}
                                label='Conversation between Daniel Roth and the Quayside team'
                                inline={askingEmail ? (
                                    <EmailCapture
                                        value={visitor.email}
                                        onChange={visitor.setEmail}
                                        onSubmit={() => void visitor.submitEmail()}
                                        disabled={visitor.stage === 'sending'}
                                        error={visitor.emailError}
                                    />
                                ) : null}
                            >
                                <ChatComposer
                                    value={visitor.draft}
                                    onChange={visitor.setDraft}
                                    onSubmit={visitor.sendMessage}
                                    onFocus={() => setAuto(false)}
                                    label='Message the Quayside team'
                                    placeholder={visitor.stage === 'done' ? "Sent. We'll be in touch soon." : 'Start typing...'}
                                    disabled={busy || visitor.stage === 'done'}
                                />
                            </ChatCard>
                        </Reveal>
                    </div>
                </div>
            </div>
        </DarkBand>
    )
}
